import { Component, computed, effect, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ILessonListItem, INotification, ISemesterNotificationContext } from '@lb/api/mylb/interfaces';
import { NotificationType } from '@lb/api/mylb/enums';
import { IEnrollmentDetailItem } from '@lb/api/mylb/interfaces/enrollment/ienrollment-detail.interface';
import { MDContentBoxComponent, MDDialogService, MDIconComponent } from '@melodic-ui/components';
import { IndicatorType } from '@melodic-ui/types';
import { SemesterGradeReviewComponent } from '../semester-grade-review/semester-grade-review.component';
import { EnrollmentNotificationsStateService } from './enrollment-notifications-state.service';
import { EnrollmentDetailStateService } from '../enrollment-detail-state.service';
import { take } from 'rxjs';

interface IEnrollmentNotification {
	id: string;
	type: IndicatorType;
	icon?: string;
	title: string;
	message: string;
	actionLabel?: string;
	action?: () => void;
}

/**
 * Enrollment overview banners — a thin projection of the enrollment's notification list from
 * Notification.Api (already role-scoped server-side). Each supported notification type maps to the
 * same banner layout this component has always rendered; types without an overview presentation
 * (e.g. reschedules, which live in the sidebar) are skipped.
 */
@Component({
	selector: 'lb-enrollment-notifications',
	standalone: true,
	imports: [CommonModule, MDContentBoxComponent, MDIconComponent],
	templateUrl: './enrollment-notifications.component.html',
	styleUrl: './enrollment-notifications.component.scss',
	providers: [EnrollmentNotificationsStateService]
})
export class EnrollmentNotificationsComponent {
	private readonly notificationsState = inject(EnrollmentNotificationsStateService);
	private readonly enrollmentDetailState = inject(EnrollmentDetailStateService);
	private readonly dialogService = inject(MDDialogService);
	private readonly router = inject(Router);

	enrollment = input.required<IEnrollmentDetailItem>();
	/** Retained for template compatibility with the enrollment overview; no longer used. */
	lessons = input<ILessonListItem[]>([]);
	lessonsLoading = input(false);

	constructor() {
		effect(() => {
			const enrollment = this.enrollment();
			if (enrollment?.enrollmentID) {
				this.notificationsState.loadNotifications(enrollment.enrollmentID);
			}
		});
	}

	notifications = computed<IEnrollmentNotification[]>(() => {
		return this.notificationsState
			.notifications()
			.map((notification) => this.toBanner(notification))
			.filter((banner): banner is IEnrollmentNotification => banner !== null);
	});

	private toBanner(notification: INotification): IEnrollmentNotification | null {
		// A not-graded enrollment (chirp room) never shows grade banners, even if stale grade
		// notifications exist in the feed from before the template was flagged.
		const gradeTypes: string[] = [NotificationType.FinalGradeSubmissionRequired, NotificationType.LessonGradesAvailable, NotificationType.SemesterGradesAvailable];
		if (!this.enrollment().isGraded && gradeTypes.includes(notification.notificationType)) {
			return null;
		}

		switch (notification.notificationType) {
			case NotificationType.FinalGradeSubmissionRequired: {
				// The backend dismisses this notification asynchronously once grades land, so a reload right
				// after submission can still return it. The refreshed detail is authoritative: a semester
				// with a final grade no longer needs one.
				const semester = this.enrollment().semesters?.find((s) => s.semesterID === notification.entityID);
				if (semester?.semesterFinalGrade) {
					return null;
				}

				const context = notification.data as ISemesterNotificationContext | null;
				const levelName = context?.levelName ?? '';
				return {
					id: `final-grade-${notification.entityID}`,
					type: (notification.severityLevel as IndicatorType) ?? 'warning',
					icon: 'untitled-ui/alerts/alert-circle',
					title: `Final grade pending for Semester ${levelName}`,
					message: `You must submit the final grade for Semester ${levelName}.`,
					actionLabel: 'Submit',
					action: () => this.submitFinalGrade(notification.entityID)
				};
			}
			case NotificationType.MidSemesterSurveyIncomplete:
				return {
					id: `mid-semester-survey-${notification.entityID}`,
					type: (notification.severityLevel as IndicatorType) ?? 'info',
					icon: 'untitled-ui/education/book-open-01',
					title: 'Survey required',
					message: 'Please complete your mid-semester survey.',
					actionLabel: 'Complete Survey',
					action: () => this.navigateToLesson(notification.entityID)
				};
			case NotificationType.LessonGradesAvailable: {
				const lesson = notification.data as ILessonListItem | null;
				return {
					id: `lesson-grades-${notification.entityID}`,
					type: (notification.severityLevel as IndicatorType) ?? 'info',
					icon: 'untitled-ui/education/book-open-01',
					title: 'Lesson grades available',
					message: 'Your grades and lesson summary are ready for review.',
					actionLabel: 'Review',
					action: () => this.navigateToLesson(notification.entityID, lesson?.enrollmentID)
				};
			}
			case NotificationType.SemesterGradesAvailable: {
				const context = notification.data as ISemesterNotificationContext | null;
				const levelName = context?.levelName ?? '';
				return {
					id: `semester-grades-${notification.entityID}`,
					type: (notification.severityLevel as IndicatorType) ?? 'success',
					icon: 'untitled-ui/education/graduation-hat-02',
					title: `Final grade available for Semester ${levelName}`,
					message: context?.finalGradeName
						? `Your final grade for Semester ${levelName} is: ${context.finalGradeName}.`
						: `Your final grade for Semester ${levelName} has been submitted.`,
					actionLabel: undefined,
					action: undefined
				};
			}
			default:
				// Sidebar-only types (reschedules, missing attendance, enrollment assignment) have no
				// overview banner presentation.
				return null;
		}
	}

	submitFinalGrade(semesterID: number): void {
		const enrollment = this.enrollment();
		if (!enrollment.gradingTemplateID || !enrollment.isGraded) return;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const dialogRef = this.dialogService.open(SemesterGradeReviewComponent as any, {
			enrollmentID: enrollment.enrollmentID,
			semesterID: semesterID
		});

		dialogRef.afterClosed.pipe(take(1)).subscribe({
			next: (result) => {
				if (result === true) {
					// Grades submitted — refresh the detail so the grades summary shows them (and the banner
					// hides), and reload notifications; the event pipeline dismisses the prompt server-side.
					this.enrollmentDetailState.refreshEnrollmentDetail(enrollment.enrollmentID);
					this.notificationsState.loadNotifications(enrollment.enrollmentID, true);
				}
			}
		});
	}

	navigateToLesson(lessonID: number, enrollmentID?: number): void {
		this.router.navigate(['/enrollments', enrollmentID ?? this.enrollment().enrollmentID, 'lessons', lessonID]);
	}
}
